import { BookingServiceClient } from 'fitbeat.api-clients';
import {
  AreaCategory,
  BookingsManager,
  HomeWorkoutSequence,
  LeaderboardConfig,
  SessionSequenceEntry,
  sleep,
  UserProfile,
  UserWorkoutSession,
  WorkoutSettings,
} from 'fitbeat.gym-core';
import { musicPlatform } from 'fitbeat.gym-core/config';
import {
  BaseUserProfile, CardioOrResistance, Equipment, IHeartRate, Instructor,
} from 'fitbeat.models';
import moment from 'moment-timezone';
import { AppState } from 'react-native';
import { Dispatch } from 'redux';
import { OPEN_PERSONALISE_WORKOUT_SETUP_SCREEN } from '../Bookings/actionTypes';
import { PersonaliseSetupScreens } from '../Bookings/steps/personaliseWorkoutSteps/types';
import { shareOpenInviteLink } from '../Bookings/utils';
import { getConfig } from '../config';
import connectedAppsManager from '../ConnectedApps';
import { GoogleAnalytics } from '../services/google-analytics';
import { analyicsForAction } from '../services/google-analytics/analyicsForAction';
import { Logger } from '../utils';
import { Permissions } from '../utils/Permissions';
import Storage from '../utils/Storage';
import {
  cleanUpMusic, genericMusicPlay,
  initialiseMusicAndStartNonGeneric, loadTracks, resetMusicPlayer,
} from './components/Music/actions';
import { disableAgoraAudio, enableAgoraAudio } from './components/VideoCalling/actions';
import { CallConnectionState } from './components/VideoCalling/reducer';
import {
  startHomeWatchAppWorkout,
  StartListeningForHeartRateData,
  verifyMetricDataIsNotStale,
} from './heartRateActions';
import { makeApiRequestAction } from './helpers';
import {
  IAppStore,
  IHomeWorkoutsStoreState,
  NUMBER_OF_SEQUENCES_UNTIL_WARMUP,
  NUMBER_OF_SEQUENCES_UNTIL_WARMUP_WITH_TALK,
  Orientation,
} from './reducer';
import { percentagePreloadedSelector, selectIsStreamingWorkout, selectStreamingHostWorkoutId } from './selectors';
import { AudioHelper, ExpoFileSystem, WorkoutAssetHelper } from './utils';
import { BluetoothServiceSingleton } from './utils/BluetoothServiceSingleton';
import { DeviceSetupScreens } from './utils/DeviceSetupScreens';
import { IAudioHelper } from './utils/IAudioHelper';
import { BluetoothState } from './utils/IBluetoothService';
import { IWorkoutAssetHelper } from './utils/IWorkoutAssetHelper';
import { createBookingServiceClient } from '../utils/apiClientFactory';
import { retryFunctionWithExponentialBackoff } from '../utils/retry-function';

export const UPDATE_WORKOUT_SCREEN = 'UPDATE_WORKOUT_SCREEN';
export const OPEN_HOME_WORKOUT_SUCCESS = 'OPEN_HOME_WORKOUT_SUCCESS';
export const OPEN_HOME_WORKOUT_REQUEST = 'OPEN_HOME_WORKOUT_REQUEST';
export const OPEN_HOME_WORKOUT_FAILURE = 'OPEN_HOME_WORKOUT_FAILURE';
export const ON_PRELOAD_OF_EXERCISES_COMPLETED = 'ON_PRELOAD_OF_EXERCISES_COMPLETED';
export const START_WORKOUT = 'START_WORKOUT';
export const OPEN_WORKOUT_SETTINGS = 'OPEN_WORKOUT_SETTINGS';
export const CLOSE_WORKOUT_SETTINGS = 'CLOSE_WORKOUT_SETTINGS';

export const OPEN_MUSIC_PLATFORMS = 'OPEN_MUSIC_PLATFORMS';
export const CLOSE_MUSIC_PLATFORMS = 'CLOSE_MUSIC_PLATFORMS';

export const CHANGE_SELECTED_INSTRUCTOR = 'CHANGE_SELECTED_INSTRUCTOR';

export const OPEN_DISPLAY_SETTINGS = 'OPEN_DISPLAY_SETTINGS';
export const CLOSE_DISPLAY_SETTINGS = 'CLOSE_DISPLAY_SETTINGS';

export const CHANGE_WORKOUT_SETTINGS = 'CHANGE_WORKOUT_SETTINGS';

export const LOAD_WORKOUT_VIDEO_SUCCESS = 'LOAD_WORKOUT_VIDEO_SUCCESS';
export const LOAD_WORKOUT_VIDEO_FAILURE = 'LOAD_WORKOUT_VIDEO_FAILURE';

export const PAUSE_HOME_WORKOUT = 'workout_pause';
export const RESUME_HOME_WORKOUT = 'workout_resume';

export const SAVE_HOME_WORKOUT_SETTINGS_REQUEST = 'SAVE_HOME_WORKOUT_SETTINGS_REQUEST';
export const SAVE_HOME_WORKOUT_SETTINGS_FAILURE = 'SAVE_HOME_WORKOUT_SETTINGS_FAILURE';
export const SAVE_HOME_WORKOUT_SETTINGS_SUCCESS = 'workout_settings_customise_change';

export const CLOSE_HOME_WORKOUT = 'CLOSE_HOME_WORKOUT';

export const PRE_WORKOUT_SCREEN_CLOSE_REQUEST = 'PRE_WORKOUT_SCREEN_CLOSE_REQUEST';
export const PRE_WORKOUT_SCREEN_CLOSE_SUCCESS = 'PRE_WORKOUT_SCREEN_CLOSE_SUCCESS';
export const PRE_WORKOUT_SCREEN_CLOSE_FAILURE = 'PRE_WORKOUT_SCREEN_CLOSE_FAILURE';

export const PRE_WORKOUT_CANCEL_REQUEST = 'PRE_WORKOUT_CANCEL_REQUEST';
export const PRE_WORKOUT_CANCEL_SUCCESS = 'PRE_WORKOUT_CANCEL_SUCCESS';
export const PRE_WORKOUT_CANCEL_FAILURE = 'PRE_WORKOUT_CANCEL_FAILURE';

export const AUDIO_TIP_PLAYED = 'AUDIO_TIP_PLAYED';

export const OPEN_CANCEL_WORKOUT = 'workout_exit_request';
export const CLOSE_CANCEL_WORKOUT = 'workout_exit_cancel';

export const COMPLETE_WORKOUT_REQUEST = 'COMPLETE_WORKOUT_REQUEST';
export const COMPLETE_WORKOUT_SUCCESS = 'COMPLETE_WORKOUT_SUCCESS';
export const RETRYING_CURRENT_VIDEO = 'RETRYING_CURRENT_VIDEO';

export const CLOSE_DEVICE_SETUP = 'CLOSE_DEVICE_SETUP';
export const OPEN_DEVICE_SETUP = 'workout_open_setup_from_workout';

export const TOGGLE_INSTRUCTOR_AUDIO_ENABLED = 'TOGGLE_INSTRUCTOR_AUDIO_ENABLED';
export const BEEP_VOLUME_CHANGED = 'BEEP_VOLUME_CHANGED';
export const VOICE_VOLUME_CHANGED = 'VOICE_VOLUME_CHANGED';

export const RESTART_ASSET_CACHING = 'RESTART_ASSET_CACHING';
export const WORKOUT_VIDEO_CACHED = 'WORKOUT_VIDEO_CACHED';
export const ON_PRELOAD_OF_EXERCISES_PROGRESS_UPDATED = 'ON_PRELOAD_OF_EXERCISES_PROGRESS_UPDATED';

export const USER_UPDATED_METRIC_INFO = 'USER_UPDATED_METRIC_INFO';

export const HOME_WORKOUT_TO_REVIEW = 'HOME_WORKOUT_TO_REVIEW';
export const SUBMIT_AFTER_WORKOUT_RATING = 'workouts_afterworkout_rate';

export const OPEN_SETUP_SCREEN = 'OPEN_SETUP_SCREEN';
export const CLOSE_SETUP_SCREEN = 'workout_back_to_setup_overview';
export const CHANGE_ORIENTATION = 'CHANGE_ORIENTATION';
export const WORKOUT_TYPE_WARNING = 'WORKOUT_TYPE_WARNING';

export const GET_INSTRUCTOR_LIST_SUCCESS = 'GET_INSTRUCTOR_LIST_SUCCESS';

export const UPDATE_WORKOUT_SETTINGS_LOADER = 'UPDATE_WORKOUT_SETTINGS_LOADER';

export const UPDATE_LEADERBOARD_REQUEST = 'UPDATE_LEADERBOARD_REQUEST';
export const UPDATE_LEADERBOARD_SUCCESS = 'UPDATE_LEADERBOARD_SUCCESS';
export const UPDATE_LEADERBOARD_FAILURE = 'UPDATE_LEADERBOARD_FAILURE';

export const ENABLE_MIRROR = 'workout_setup_video_mirror_enabled';
export const DISABLE_MIRROR = 'workout_setup_video_mirror_disabled';

export const ON_WARMUPORFIRSTEXERCISE_JUST_STARTED_PLAYING = 'ON_WARMUPORFIRSTEXERCISE_JUST_STARTED_PLAYING';
export const LEADERBOARD_LAYOUT_CHANGED = 'LEADERBOARD_LAYOUT_CHANGED';

export const NAVIGATE_TO_DASHBOARD_AFTER_REVIEW = 'NAVIGATE_TO_DASHBOARD_AFTER_REVIEW';

export const SET_DEFAULT_INSTRUCTOR = 'workout_setup_instructor_default_change';
export const SAVE_AND_CLOSE_INSTRUCTOR_SCREEN_REQUEST = 'workout_setup_instructor_change';

export const CLOSE_INSTRUCTOR_AND_DISCARD_CHANGES = 'CLOSE_INSTRUCTOR_AND_DISCARD_CHANGES';
export const WORKOUT_EXIT_AND_SAVE = 'workout_exit_and_save';
export const WORKOUT_EXIT_AND_DISCARD = 'workout_exit_and_discard';
export const HEART_RATE_AUTO_CONNECTED = 'workout_setup_heart_rate_auto_connect';

export const WORKOUT_SCREEN_TAPPED = 'WORKOUT_SCREEN_TAPPED';

export const NUMBER_OF_SEQUENCES_TO_PRELOAD = 3;

export const ACTIVATE_LEADERBOARD = 'ACTIVATE_LEADERBOARD';
export const DEACTIVATE_LEADERBOARD = 'DEACTIVATE_LEADERBOARD';

export const INVITE_FRIENDS_REQUEST = 'invite_friends_pressed';
export const INVITE_FRIENDS_SUCCESS = 'invite_friends_success';
export const INVITE_FRIENDS_FAILURE = 'invite_friends_failure';

export const HOME_WARMDOWN_STARTED_REQUEST = 'HOME_WARMDOWN_STARTED_REQUEST';
export const HOME_WARMDOWN_STARTED_SUCCESS = 'HOME_WARMDOWN_STARTED_SUCCESS';
export const HOME_WARMDOWN_STARTED_FAILURE = 'HOME_WARMDOWN_STARTED_FAILURE';

export const MIRROR_SETUP_SCREEN_OPENED = 'mirror_setup_screen_opened';

export const SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_REQUEST = 'SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_REQUEST';
export const SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_SUCCESS = 'SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_SUCCESS';
export const SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_FAILURE = 'SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_FAILURE';

export const SET_USER_SETTINGS_OPTION_REQUEST = 'SET_USER_SETTINGS_OPTION_REQUEST';
export const SET_USER_SETTINGS_OPTION_SUCCESS = 'SET_USER_SETTINGS_OPTION_SUCCESS';
export const SET_USER_SETTINGS_OPTION_FAILURE = 'SET_USER_SETTINGS_OPTION_FAILURE';

const OPENED_SCREEN_CASTING = 'OPENED_SCREEN_CASTING';

export const updateWorkoutScreen = (
  currentTime: Date,
  workoutAssetHelper: IWorkoutAssetHelper,
  audioHelper: IAudioHelper,
  bookingServiceClient: BookingServiceClient,
  workoutSessionService: any,
  debug: string,
) => {
  const config = getConfig();

  return async (dispatch: any, getState: () => IAppStore) => {
    const stateBeforeTick = getState();
    const {
      screenMirroringModalVisible,
      heartRateMonitorModalVisible,
    } = stateBeforeTick.ui;
    if (stateBeforeTick.homeWorkouts.workoutSettingsIsUpdating
      || stateBeforeTick.homeWorkouts.workoutSettingsModalIsOpen
      || stateBeforeTick.homeWorkouts.MusicPlatformsModelIsOpen
      || screenMirroringModalVisible
      || heartRateMonitorModalVisible) {
      return;
    }
    dispatch({
      type: UPDATE_WORKOUT_SCREEN,
      payload: {
        currentTime,
        callIsConnected: stateBeforeTick.groupCalling.connectionState === CallConnectionState.connected,
        debug,
      },
    });

    if (!config.workoutUpdateActionEnabled) {
      Logger.debug('Workout action:updateWorkoutScreen is disabled');
      return;
    }

    const stateAfterTick = getState();

    if (stateAfterTick.homeWorkouts.shouldLoadNextVideo && config.showWorkoutVideo) {
      loadWorkoutVideo(
        stateAfterTick.homeWorkouts.homeWorkoutSession.workoutId,
        stateAfterTick.homeWorkouts.currentSessionSequenceInfo.currentSequence,
        workoutAssetHelper,
        async () => {
          await recoverFromLoadWorkoutVideoFailure(
            audioHelper,
            bookingServiceClient,
            workoutAssetHelper,
            stateAfterTick.homeWorkouts,
            dispatch,
            getState,
          );
        },
        dispatch,
      )
        .catch((e) => Logger.notify(e));
      Logger.debug(`updateWorkoutScreen ${debug}: delay ${config.delayWhenLoadingWorkoutVideo}`);
      await sleep(config.delayWhenLoadingWorkoutVideo);
    }

    // TODO: merge with other canUseConnectedAppForHeartRate checks
    const canUseConnectedAppsForHR = await connectedAppsManager.canUseConnectedAppForHeartRate();
    if (stateAfterTick.homeWorkouts.userProfile
      && (stateAfterTick.homeWorkouts.userProfile.heartRateMonitors?.home || canUseConnectedAppsForHR)
      && config.allowBluetoothService) {
      dispatch(verifyMetricDataIsNotStale(
        stateAfterTick.homeWorkouts.homeWorkoutSession,
        new Date(),
        stateAfterTick.homeWorkouts.workoutPauseStartTime,
      ));
      restartBeltProcessIfMetricsHasGoneStale(
        stateAfterTick,
        getState(),
        dispatch,
      );
    }

    if (!stateAfterTick.homeWorkouts.currentSessionSequenceInfo) {
      return;
    }

    if (!stateAfterTick.homeWorkouts.homeWorkoutSession) {
      return;
    }

    if (stateAfterTick.homeWorkouts.audioTipToPlay
      && config.playWorkoutAudioTips
    ) {
      playAudioTip(
        stateAfterTick.homeWorkouts.homeWorkoutSession,
        stateAfterTick.homeWorkouts.audioTipToPlay,
        stateAfterTick.homeWorkouts.currentSessionSequenceInfo.currentSequence.exerciseVersion,
        stateAfterTick.homeWorkouts.voiceVolume,
        stateAfterTick.homeWorkouts.currentSessionSequenceInfo.currentSequence.audioTipShouldCache,
        audioHelper,
        workoutAssetHelper,
        dispatch,
      )
        .catch((e) => Logger.notify(e));
    }

    if (stateAfterTick.homeWorkouts.shouldSaveMetrics) {
      dispatch(completeWorkout(bookingServiceClient, workoutSessionService, false, false));
      if (config.autoEndWorkout) {
        Logger.debug('Workout auto ending now');
        dispatch(completeWorkout(bookingServiceClient, workoutSessionService, /* should close workout */ true, false));
        dispatch(workoutReviewed());
      }
    }

    if (hasPreloadOfExercisesJustCompleted(stateAfterTick)) {
      await onPreloadOfExercisesCompleted(stateAfterTick, dispatch);
      if (config.autoStartWorkout) {
        Logger.debug('Workout auto starting now');
        dispatch(startWorkout(bookingServiceClient, audioHelper, () => {
          // initial group call handling not implemented for auto start
        }));
      }
    }

    if (hasWarmupOrFirstExerciseStartedPlaying(stateAfterTick.homeWorkouts, currentTime)) {
      await onWarmupOrFirstExerciseJustStartedPlaying(stateAfterTick, dispatch);
    }

    if (hasWarmdownStartedPlaying(stateAfterTick.homeWorkouts)) {
      await onWarmdownHasStartedPlaying(bookingServiceClient, stateAfterTick, dispatch);
    }

    stopMusicAndEnableTalkingIfWorkoutEnded(stateBeforeTick, stateAfterTick, dispatch);

    const paddingToFetchLeaderboardMS = LeaderboardConfig.secondInterval * 1000;
    const { leaderboardFetchedAt, isFetchingLeaderboard } = stateAfterTick.homeWorkouts;
    // todo: perhaps this should be disabled based on user leaderboard enabled setting
    if (!isFetchingLeaderboard
      && ((Date.now()) - leaderboardFetchedAt.getTime()) > paddingToFetchLeaderboardMS
      && config.enableWorkoutLeaderboardUpdates) {
      CalculatedMetricsHasChanged(bookingServiceClient, stateAfterTick, dispatch)
        .catch((e) => Logger.notify(e));
    }

    Logger.debug(`Workout timings: action:updateWorkoutScreen ${debug} done in ${Date.now() - currentTime.getTime()}`);
  };
};

function hasWarmupOrFirstExerciseStartedPlaying(homeWorkout: IHomeWorkoutsStoreState, now: Date): boolean {
  const hasEventAlreadyOccurred = homeWorkout.warmupOrFirstExerciseHasStartedPlaying;
  if (hasEventAlreadyOccurred) {
    return false;
  }
  const hasWorkoutStartedPlaying = homeWorkout.userHasStartedWorkout;
  if (!hasWorkoutStartedPlaying) {
    return false;
  }
  if (!homeWorkout.homeWorkoutSession) {
    return false;
  }
  if (homeWorkout.homeWorkoutSession.livestreamOptions?.preWorkoutTalk) {
    return (homeWorkout.currentSessionSequenceInfo?.currentIndex || 0) >= NUMBER_OF_SEQUENCES_UNTIL_WARMUP_WITH_TALK;
  }
  return (homeWorkout.currentSessionSequenceInfo?.currentIndex || 0) >= NUMBER_OF_SEQUENCES_UNTIL_WARMUP;
}

function hasWarmdownStartedPlaying(homeWorkout: IHomeWorkoutsStoreState): boolean {
  return homeWorkout.inOrPastWarmdown;
}

function hasPreloadOfExercisesJustCompleted(state: IAppStore): boolean {
  if (!state.homeWorkouts.preloadOfExercisesLoading) {
    return false; // event already happened
  }

  return percentagePreloadedSelector(state) >= 100;
}

export const pauseWorkout = (audioHelper: IAudioHelper) => async (dispatch: any, getState: () => IAppStore) => {
  const state = getState();
  if (state.homeWorkouts.isGroupWorkout) {
    return;
  }

  const pauseStartTime = new Date();
  audioHelper.toggleCurrentPlayingTipPaused(true)
    .catch((e) => Logger.notify(e));
  await connectedAppsManager.pauseWorkout();
  return dispatch({
    type: PAUSE_HOME_WORKOUT,
    payload: {
      pauseStartTime,
    },
    ...analyicsForAction(PAUSE_HOME_WORKOUT),
  });
};

export const workoutScreenTapped = (
  audioHelper: IAudioHelper,
  bookingServiceClient: BookingServiceClient,
) => (dispatch: any, getState: () => IAppStore) => {
  const state = getState();

  dispatch({ type: WORKOUT_SCREEN_TAPPED });

  const isWorkoutPaused = !!state.homeWorkouts.workoutPauseStartTime;
  if (isWorkoutPaused) {
    dispatch(resumeWorkout(bookingServiceClient, audioHelper));
  } else {
    dispatch(pauseWorkout(audioHelper));
  }
};

export const resumeWorkout = (
  bookingServiceClient: any,
  audioHelper: IAudioHelper,
) => async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  const resumeTime = new Date();
  const currentState = getState() as IAppStore;
  const { homeWorkoutSession, workoutPauseStartTime } = currentState.homeWorkouts;
  if (!homeWorkoutSession) {
    return;
  }
  bookingServiceClient.resumeHomeWorkout(
    homeWorkoutSession.workoutId,
    workoutPauseStartTime && workoutPauseStartTime.getTime(),
    resumeTime.getTime(),
  );
  audioHelper.toggleCurrentPlayingTipPaused(false)
    .catch((e) => Logger.notify(e));
  await connectedAppsManager.resumeWorkout();
  dispatch({
    type: RESUME_HOME_WORKOUT,
    payload: {
      resumeTime,
    },
    ...analyicsForAction(RESUME_HOME_WORKOUT),
  });
};

export interface IOpenHomeWorkoutRequest {
  type: typeof OPEN_HOME_WORKOUT_REQUEST;
}

export const openHomeWorkout = (
  booking: UserWorkoutSession,
  bookingServiceClient: BookingServiceClient,
  workoutAssetHelper: IWorkoutAssetHelper,
  userProfile: UserProfile,
  debug: string | undefined,
) => async (dispatch: any, getState: () => IAppStore) => {
  const start = Date.now();

  try {
    Logger.debug(`Workout action:openHomeWorkout ${debug} starting`);
    dispatch({
      type: OPEN_HOME_WORKOUT_REQUEST,
      payload: {
        userProfile,
        booking,
        defaultLeaderBoardState: await Storage.get('defaultLeaderBoardState', true),
        defaultMirrorStateFromLocalStorage: await Storage.get('defaultMirrorState'),
        hasOpenedScreenCasting: !!(await Storage.get(OPENED_SCREEN_CASTING)),
        hasCameraPermission: await Permissions.isCameraPermissionGranted(),
      },
    });
    Logger.debug(`Workout action:openHomeWorkout ${debug} dispatched OPEN_HOME_WORKOUT_REQUEST`);

    const now = new Date();
    const timezone = moment.tz.guess();
    const homeWorkoutSession = await bookingServiceClient.getHydratedHomeWorkout(
      booking.uniqueReference,
      timezone,
      getConfig().workoutPlaybackSpeed,
    );
    Logger.debug(`Workout action:openHomeWorkout ${debug} getHydratedHomeWorkout done`);

    const workoutHasClosedSinceLoadStarted = getState().homeWorkouts.shouldCloseWorkout;
    if (workoutHasClosedSinceLoadStarted) {
      Logger.debug(`Workout action:openHomeWorkout ${debug} workoutHasClosedSinceLoadStarted`);
      return;
    }

    const workoutLoadDifference = Date.now() - now.getTime();
    dispatch({
      type: OPEN_HOME_WORKOUT_SUCCESS,
      payload: {
        homeWorkoutSession,
        homeWorkoutId: booking.uniqueReference,
        now: new Date(),
        workoutLoadDifference,
      },
    });
    Logger.debug(`Workout action:openHomeWorkout ${debug} dispatched OPEN_HOME_WORKOUT_SUCCESS`);

    const currentState = getState() as IAppStore;
    const currentIndex = currentState.homeWorkouts.currentSessionSequenceInfo
      ? currentState.homeWorkouts.currentSessionSequenceInfo.currentIndex : 0;
    await AudioHelper.setupAudioMode();
    await startAssetCaching(userProfile, homeWorkoutSession, currentIndex, workoutAssetHelper, dispatch, getState);
    await resetMusicPlayer(dispatch);
    Logger.debug(`Workout action:openHomeWorkout ${debug} awaited (audio, assets, music)`);

    bookingServiceClient.getAllInstructors().then((response) => {
      dispatch(getInstructorListSuccess(
        response.instructors,
        userProfile.homeWorkoutInstructor,
        homeWorkoutSession.workoutSettings.homeWorkoutInstructor,
      ));
      const time = Date.now() - start;
      Logger.debug(`Workout action:openHomeWorkout ${debug} getAllInstructors done (from start: ${time})`);
      return response.instructors;
    }).catch((error) => {
      Logger.notify(error);
    });

    await CalculatedMetricsHasChanged(bookingServiceClient, currentState, dispatch);
  } catch (error) {
    Logger.notify(error);
    dispatch({
      type: OPEN_HOME_WORKOUT_FAILURE,
      payload: 'Unable to load your workout',
    });
  } finally {
    const time = Date.now() - start;
    Logger.debug(`Workout action:openHomeWorkout ${debug} done in ${time}`);
  }
};

export const startWorkout = (
  bookingServiceClient: BookingServiceClient,
  audioHelper: IAudioHelper,
  initiateGroupCall: (pairedSessionReference: string) => void,
) => async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  const state = getState();
  const {
    homeWorkoutId, homeWorkoutSession, isGroupWorkout: shouldInitiateGroupCall,
    livestreamOptions,
  } = state.homeWorkouts;
  if (!homeWorkoutSession) {
    return;
  }

  if (shouldInitiateGroupCall) {
    initiateGroupCall(homeWorkoutSession.groupCallPairedId!);
  }

  dispatch(resumeWorkout(bookingServiceClient, audioHelper));
  await connectedAppsManager.startWorkout();
  const { isDemoBooking } = homeWorkoutSession;

  const isStreamingWorkoutWithoutATalkAtTheStart = (
    homeWorkoutSession.isStreamingWorkout && !livestreamOptions?.preWorkoutTalk
  );
  const shouldStartMusic = isStreamingWorkoutWithoutATalkAtTheStart || !state.homeWorkouts.isGroupWorkout;
  if (state.music.musicPlayer === musicPlatform.FitbeatMusic
      && shouldStartMusic) {
    if (isStreamingWorkoutWithoutATalkAtTheStart) {
      const config = getConfig();
      await sleep(config.streamingWorkoutMusicDelayInMs);
    }
    dispatch(genericMusicPlay());
  }

  dispatch({
    type: START_WORKOUT,
    ...analyicsForAction(isDemoBooking ? 'sign_up_start_now' : 'workout_start_now', { homeWorkoutId }),
  });
};

const getBookingIdToDelete = (state: IAppStore): string | void => {
  const { homeWorkouts: { homeWorkoutSession } } = state;

  const shouldDeleteBooking = homeWorkoutSession
    && (homeWorkoutSession.isInstantBooking || homeWorkoutSession.isInstant);
  if (homeWorkoutSession && shouldDeleteBooking) {
    return homeWorkoutSession.workoutId
      ? homeWorkoutSession.workoutId : homeWorkoutSession.uniqueReference;
  }
};

export const closeHomeWorkout = () => async (dispatch) => {
  WorkoutAssetHelper.stopCurrentRunningCachingProcess();
  await connectedAppsManager.stopAndDiscardWorkout();
  cleanUpMusic(dispatch)
    .catch((e) => Logger.notify(e));
  AudioHelper.clearCachedItems('closeHomeWorkout')
    .catch((e) => Logger.notify(e));

  dispatch({
    type: CLOSE_HOME_WORKOUT,
    ...analyicsForAction('workout_exit_prestart_back'),
  });
};

export const preWorkoutSummaryScreenClosed = (bookingServiceClient: BookingServiceClient) => (
  async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
    try {
      const deleteBookingId = getBookingIdToDelete(getState());
      dispatch({
        type: PRE_WORKOUT_SCREEN_CLOSE_REQUEST,
        payload: {
          deleteBookingId,
        },
        ...analyicsForAction(PRE_WORKOUT_SCREEN_CLOSE_REQUEST),
      });
      if (deleteBookingId) {
        await bookingServiceClient.cancelBookingAndReturnBookingList(deleteBookingId);
      }
      await connectedAppsManager.stopAndDiscardWorkout();
      dispatch({ type: PRE_WORKOUT_SCREEN_CLOSE_SUCCESS });
    } catch {
      dispatch({ type: PRE_WORKOUT_SCREEN_CLOSE_FAILURE });
    }
  }
);

export const preWorkoutSummaryCancelWorkoutPressed = (bookingServiceClient: BookingServiceClient) => (
  async (
    dispatch: Dispatch<IPreWorkoutCancelRequest | IPreWorkoutCancelSuccess | IPreWorkoutCancelFailure>,
    getState: () => IAppStore,
  ) => {
    try {
      const { homeWorkouts: { homeWorkoutSession } } = getState();
      const deleteBookingId = homeWorkoutSession!.workoutId;
      dispatch({
        type: PRE_WORKOUT_CANCEL_REQUEST,
        payload: {
          deleteBookingId,
        },
        ...analyicsForAction(PRE_WORKOUT_CANCEL_REQUEST),
      });
      await bookingServiceClient.cancelBookingAndReturnBookingList(deleteBookingId);
      await connectedAppsManager.stopAndDiscardWorkout();
      dispatch({ type: PRE_WORKOUT_CANCEL_SUCCESS });
    } catch {
      dispatch({ type: PRE_WORKOUT_CANCEL_FAILURE });
    }
  }
);

export const saveWorkoutSettings = makeApiRequestAction({
  requestType: SAVE_HOME_WORKOUT_SETTINGS_REQUEST,
  failureType: SAVE_HOME_WORKOUT_SETTINGS_FAILURE,
}, async (
  bookingServiceClient: BookingServiceClient,
  workoutAssetHelper: IWorkoutAssetHelper,
  setDefaultEquipment: boolean,
  dispatch?: any,
  getState?: any,
) => {
  const currentState = getState() as IAppStore;
  if (!currentState.homeWorkouts.homeWorkoutSession) {
    return;
  }

  await bookingServiceClient.resetWorkoutSettings(
    currentState.homeWorkouts.homeWorkoutSession.workoutId,
    currentState.homeWorkouts.updatedWorkoutSettings,
    true,
    setDefaultEquipment,
  );
  await updateWorkoutSettings(
    bookingServiceClient,
    currentState.homeWorkouts.homeWorkoutSession.workoutId,
    dispatch,
    getState,
    workoutAssetHelper,
    currentState.homeWorkouts.homeWorkoutSession,
  );
});

// Note: this function was created to allow beta user to test static studio bench and dumbbell workouts
// if you find yourself extending this function please consider refactoring it so that it makes sense in
// the context of a normal home workout
export const setBetaUserBenchAndDumbbellsAction = () => async (
  dispatch: Dispatch<any>,
  getState: () => IAppStore,
) => {
  try {
    dispatch({ type: SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_REQUEST });
    const bookingServiceClient = createBookingServiceClient();
    const currentState = getState();
    if (!currentState.homeWorkouts.homeWorkoutSession) {
      return;
    }
    const updatedSettings = {
      ...currentState.homeWorkouts.homeWorkoutSession.workoutSettings,
      equipment: [Equipment.Bench, Equipment.Bodyweight, Equipment.Dumbbells, Equipment.Pushbells],
    };
    await bookingServiceClient.resetWorkoutSettings(
      currentState.homeWorkouts.homeWorkoutSession.workoutId,
      updatedSettings,
      true,
      false,
    );
    const workoutAssetHelper = new WorkoutAssetHelper(new ExpoFileSystem());
    await updateWorkoutSettings(
      bookingServiceClient,
      currentState.homeWorkouts.homeWorkoutSession.workoutId,
      dispatch,
      getState,
      workoutAssetHelper,
      currentState.homeWorkouts.homeWorkoutSession,
    );
    dispatch({ type: SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_SUCCESS });
  } catch {
    dispatch({ type: SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_FAILURE });
  }
};

export const setBetaUserOptionAction = (areaToMimic: AreaCategory) => async (
  dispatch: Dispatch<any>,
  getState: () => IAppStore,
) => {
  try {
    dispatch({ type: SET_USER_SETTINGS_OPTION_REQUEST });
    const bookingServiceClient = createBookingServiceClient();
    const currentState = getState();
    if (!currentState.homeWorkouts.homeWorkoutSession) {
      return;
    }
    const updatedSettings = getSettingsForAreaCategory(
      currentState.homeWorkouts.homeWorkoutSession.workoutSettings,
      areaToMimic,
    );
    await bookingServiceClient.resetWorkoutSettings(
      currentState.homeWorkouts.homeWorkoutSession.workoutId,
      updatedSettings,
      true,
      false,
    );
    const workoutAssetHelper = new WorkoutAssetHelper(new ExpoFileSystem());
    await updateWorkoutSettings(
      bookingServiceClient,
      currentState.homeWorkouts.homeWorkoutSession.workoutId,
      dispatch,
      getState,
      workoutAssetHelper,
      currentState.homeWorkouts.homeWorkoutSession,
    );
    dispatch({ type: SET_USER_SETTINGS_OPTION_SUCCESS });
  } catch {
    dispatch({ type: SET_USER_SETTINGS_OPTION_FAILURE });
  }
};

const getSettingsForAreaCategory = (settings: WorkoutSettings, areaCategory: AreaCategory) => {
  switch (areaCategory) {
    case 'boxing': {
      return {
        ...settings,
        equipment: [Equipment.Bodyweight],
        allowBagContact: true,
        cardioOrResistance: CardioOrResistance.Cardio,
      };
    }
  }
};

export const openWorkoutSettings = () => ({
  type: OPEN_WORKOUT_SETTINGS,
});

export const changeWorkoutSettings = (newWorkoutSettings: WorkoutSettings) => ({
  type: CHANGE_WORKOUT_SETTINGS,
  payload: {
    newWorkoutSettings,
  },
});

export const openMusicPlatforms = () => ({
  type: OPEN_MUSIC_PLATFORMS,
});

export const closeMusicPlatforms = () => ({
  type: CLOSE_MUSIC_PLATFORMS,
});

export const setWorkoutInstructor = (
  workoutId: string,
  selectedInstructor: Instructor,
  bookingServiceClient: BookingServiceClient,
) => async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  try {
    const currentState = getState();
    const currentInstructor = currentState.homeWorkouts.selectedInstructor
      ? currentState.homeWorkouts.selectedInstructor.id : '';
    if (currentInstructor !== selectedInstructor.id) {
      dispatch(changeSelectedInstructor(selectedInstructor));
      if (currentState.homeWorkouts.defaultInstructor) {
        await bookingServiceClient.setDefaultInstructor('');
      }
    }
  } catch (error) {
    Logger.leaveBreadcrumb('Failed to set instructor ', error);
    dispatch(changeSelectedInstructor(null));
  }
};

export const changeSelectedInstructor = (selectedInstructor: Instructor | null) => ({
  type: CHANGE_SELECTED_INSTRUCTOR,
  payload: {
    selectedInstructor,
  },
});

export const openDisplaySettings = () => ({
  type: OPEN_DISPLAY_SETTINGS,
});

export const closeDisplaySettings = () => ({
  type: CLOSE_DISPLAY_SETTINGS,
});

export const openCancelWorkout = () => ({
  type: OPEN_CANCEL_WORKOUT,
  ...analyicsForAction(OPEN_CANCEL_WORKOUT),
});

export const closeCancelWorkout = () => ({
  type: CLOSE_CANCEL_WORKOUT,
  ...analyicsForAction(CLOSE_CANCEL_WORKOUT),
});

export const closeWorkoutSettings = () => ({
  type: CLOSE_WORKOUT_SETTINGS,
});

export const completeWorkoutRequest = (showLoading: boolean, workoutId: string) => ({
  type: COMPLETE_WORKOUT_REQUEST,
  payload: {
    showLoading,
    workoutId,
  },
});

export const completeWorkoutSuccess = () => ({
  type: COMPLETE_WORKOUT_SUCCESS,
});

export const openDeviceSetup = () => (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  const state = getState();
  const { homeWorkoutId, homeWorkoutSession } = state.homeWorkouts;
  dispatch({
    type: OPEN_DEVICE_SETUP,
    ...analyicsForAction(OPEN_DEVICE_SETUP),
  });

  if (homeWorkoutSession) {
    const { isDemoBooking } = homeWorkoutSession;
    const googleAnalytics = new GoogleAnalytics();
    const analyticsEvent = isDemoBooking ? 'sign_up_setup_devices' : 'workout_setup_devices';
    googleAnalytics.logEventWithParamaters(analyticsEvent, { homeWorkoutId });
  }
};

export const closeDeviceSetup = () => (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  const state = getState();
  dispatch({
    type: CLOSE_DEVICE_SETUP,
  });
};

export const completeWorkout = (
  bookingServiceClient: BookingServiceClient,
  workoutSessionService: any,
  shouldCloseHomeWorkout: boolean,
  showLoading: boolean,
  navigateToDashboardAfterReview = true,
) => async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  Logger.debug('Workout action:completeWorkout');

  const state = getState();
  const { metricResult, heartRateDataPoints } = state.heartRateMonitor;
  const {
    homeWorkoutId, homeWorkoutSession, hasSavedMetrics, isSavingMetrics,
  } = state.homeWorkouts;
  if (!homeWorkoutId) {
    return;
  }

  const workoutAlreadyCompleted = hasSavedMetrics || isSavingMetrics;
  if (!workoutAlreadyCompleted) {
    Logger.debug('Workout action:completeWorkout saving');
    dispatch(completeWorkoutRequest(showLoading, homeWorkoutId));
    const mapedHeartRatePoints = mapHeartRatePointsToRawData(heartRateDataPoints);

    try {
      const completedWorkout = await retryFunctionWithExponentialBackoff(
        () => bookingServiceClient.completeWorkout(
          homeWorkoutId,
          metricResult,
          mapedHeartRatePoints,
        ),
        3,
      );
      await Promise.all([
        workoutSessionService.getMyCurrentBookings(),
        connectedAppsManager.publishWorkoutSessionToConnectedApps(completedWorkout),
      ]);
    } catch (error) {
      Logger.notify(`Failed to complete home workout: ${homeWorkoutId}`);
      throw(error);
    }
    dispatch(completeWorkoutSuccess());
  }

  if (shouldCloseHomeWorkout) {
    Logger.debug('Workout action:completeWorkout closing home workout');
    dispatch(closeHomeWorkout());
    dispatch(launchHomeWorkoutReviewModal(homeWorkoutSession, navigateToDashboardAfterReview));
    dispatch({
      type: WORKOUT_EXIT_AND_SAVE,
      ...analyicsForAction(WORKOUT_EXIT_AND_SAVE),
    });
  }

  if (!workoutAlreadyCompleted && homeWorkoutSession) {
    Logger.debug('Workout action:completeWorkout homeWorkoutSession logging');
    const { isDemoBooking } = homeWorkoutSession;
    const googleAnalytics = new GoogleAnalytics();
    const analyticsEvent = isDemoBooking ? 'sign_up_workout_completed' : 'workout_completed';
    googleAnalytics.logEventWithParamaters(analyticsEvent, {
      workoutId: homeWorkoutSession && homeWorkoutSession.workoutId,
      startTime: homeWorkoutSession && homeWorkoutSession.startTime,
      endTime: homeWorkoutSession && homeWorkoutSession.endTime,
    });
  }
};

export const getInstructorListSuccess = (
  instructorList: Instructor[],
  defaultInstructor: string | undefined,
  workoutSessionInstructor: string | undefined,
) => ({
  type: GET_INSTRUCTOR_LIST_SUCCESS,
  payload: { instructorList, defaultInstructor, workoutSessionInstructor },
});

export const changeDefaultInstructor = (
  userId: string,
  bookingServiceClient: BookingServiceClient,
) => async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  const currentState = getState();
  const { defaultInstructor } = currentState.homeWorkouts;
  const selectedInstructor = currentState.homeWorkouts.selectedInstructor
    ? currentState.homeWorkouts.selectedInstructor.id : '';
  const newDefaultInstructor = defaultInstructor ? '' : selectedInstructor;
  dispatch(setDefaultInstructor(newDefaultInstructor));
  try {
    await bookingServiceClient.setDefaultInstructor(newDefaultInstructor);
  } catch (err) {
    dispatch(setDefaultInstructor(''));
  }
};

export const setDefaultInstructor = (instructorId: string) => ({
  type: SET_DEFAULT_INSTRUCTOR,
  payload: { instructorId },
  ...analyicsForAction(SET_DEFAULT_INSTRUCTOR),
});

export const launchHomeWorkoutReviewModal = (workoutSession: any, navigateToDashboardAfterReview) => ({
  type: HOME_WORKOUT_TO_REVIEW,
  payload: {
    workoutId: workoutSession.workoutId,
    isDemoBooking: workoutSession.isDemoBooking,
    navigateToDashboardAfterReview,
  },
});

export const workoutReviewed = () => ({
  type: HOME_WORKOUT_TO_REVIEW,
  payload: null,
});

export const submitAfterWorkoutRating = (rating, comment) => ({
  type: SUBMIT_AFTER_WORKOUT_RATING,
  ...analyicsForAction(SUBMIT_AFTER_WORKOUT_RATING, { rating, comment }),
});

export const discardWorkout = (
  bookingServiceClient: BookingServiceClient,
  workoutSessionService: any,
) => async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  const state = getState();
  const { heartRateDataPoints } = state.heartRateMonitor;
  const { homeWorkoutId } = state.homeWorkouts;
  if (!homeWorkoutId) {
    return;
  }

  dispatch(completeWorkoutRequest(true, homeWorkoutId));
  const mapedHeartRatePoints = mapHeartRatePointsToRawData(heartRateDataPoints);
  await bookingServiceClient.discardWorkout(homeWorkoutId, mapedHeartRatePoints);
  await connectedAppsManager.stopAndDiscardWorkout();
  await workoutSessionService.getMyCurrentBookings();
  dispatch(completeWorkoutSuccess());
  dispatch(closeHomeWorkout());
  //
  dispatch({
    type: WORKOUT_EXIT_AND_DISCARD,
    ...analyicsForAction(WORKOUT_EXIT_AND_DISCARD),
  });
};

export const leaveWorkoutClicked = (
  bookingServiceClient: BookingServiceClient,
  workoutSessionService: any,
) => async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  const state = getState();
  const { inOrPastWarmdown, warmupOrFirstExerciseHasStartedPlaying } = state.homeWorkouts;
  if (!warmupOrFirstExerciseHasStartedPlaying) {
    dispatch(closeHomeWorkout());
    return;
  }

  if (inOrPastWarmdown) {
    const shouldCloseHomeWorkout = true;
    const shouldShowLoading = true;
    dispatch(completeWorkout(bookingServiceClient, workoutSessionService, shouldCloseHomeWorkout, shouldShowLoading));
    return;
  }
  dispatch(openCancelWorkout());
};

export const errorPlayingVideo = (
  errorString: string,
  workoutAssetHelper: IWorkoutAssetHelper,
) => (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  const audioSessionNotActivatedError = errorString.indexOf('audio session not activated') !== -1;
  if (audioSessionNotActivatedError) {
    Logger.leaveBreadcrumb(`Audio session not activated (possibly due to app backgrounded): ${errorString}`);
    const ignoreErrorIfAppIsInBackground = AppState.currentState === 'background';
    if (ignoreErrorIfAppIsInBackground) {
      Logger.leaveBreadcrumb('Ignoring error since the app is in the background');
      return;
    }
  }

  const currentState = getState();
  Logger.notify(new Error('Error playing video'), (report: any) => {
    report.errorClass = 'Home workout video failed to play';
    report.errorMessage = 'Video playing error:'
        + `${errorString},`
        + `url: ${currentState.homeWorkouts.currentPlayingVideo}`;
    report.retrying = true;
  });

  // error handling will retry playing the video
  dispatch({
    type: LOAD_WORKOUT_VIDEO_FAILURE,
    payload: {
    },
  });
};

export const toggleInstructorAudioEnabled = (enabled: boolean) => ({
  type: TOGGLE_INSTRUCTOR_AUDIO_ENABLED,
  payload: {
    enabled,
  },
});

export const beepVolumeChanged = (volume: number): IVolumeChangedAction => ({
  type: BEEP_VOLUME_CHANGED,
  payload: {
    volume,
  },
});

export const voiceVolumeChanged = (volume: number): IVolumeChangedAction => ({
  type: VOICE_VOLUME_CHANGED,
  payload: {
    volume,
  },
});

export const userUpdatedMetricInfo = (userProfile: BaseUserProfile) => ({
  type: USER_UPDATED_METRIC_INFO,
  payload: {
    userProfile,
  },
});

export const openSetupScreen = (screen: DeviceSetupScreens) => {
  if (screen === DeviceSetupScreens.ScreenCasting) {
    Storage.set(OPENED_SCREEN_CASTING, 'true')
      .catch((e) => Logger.notify(e));
  }

  const eventName = `workout_setup_open_${screen}`;
  return {
    type: OPEN_SETUP_SCREEN,
    payload: {
      screen,
    },
    ...analyicsForAction(eventName),
  };
};

interface IWorkoutTypeWarningActionParams {
  cardioOrResistance: CardioOrResistance;
  screen: DeviceSetupScreens;
}
export const workoutTypeChanged = (actionParams:IWorkoutTypeWarningActionParams) => (dispatch: any) => {
  const { screen, cardioOrResistance } = actionParams;
  dispatch(openSetupScreen(screen));
  dispatch({
    type: WORKOUT_TYPE_WARNING,
    payload: {
      cardioOrResistance,
    },
  });
};

export const updateWorkoutSettingsLoader = (status: boolean) => ({
  type: UPDATE_WORKOUT_SETTINGS_LOADER,
  payload: status,
});

export const saveAndCloseInstructorScreen = (
  bookingServiceClient: BookingServiceClient,
  workoutAssetHelper: IWorkoutAssetHelper,
) => async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  try {
    dispatch({
      type: SAVE_AND_CLOSE_INSTRUCTOR_SCREEN_REQUEST,
      ...analyicsForAction(SAVE_AND_CLOSE_INSTRUCTOR_SCREEN_REQUEST),
    });
    const currentState = getState() as IAppStore;
    if (!currentState.homeWorkouts.homeWorkoutSession) {
      return;
    }
    if (
      (currentState.homeWorkouts.selectedInstructor && currentState.homeWorkouts.homeWorkoutSession)
        && (currentState.homeWorkouts.selectedInstructor.id
          !== currentState.homeWorkouts.homeWorkoutSession.workoutSettings.homeWorkoutInstructor)) {
      dispatch(updateWorkoutSettingsLoader(true));
      await bookingServiceClient.setWorkoutInstructor(
        currentState.homeWorkouts.homeWorkoutSession.workoutId,
        currentState.homeWorkouts.selectedInstructor.id,
      );
      await updateWorkoutSettings(
        bookingServiceClient,
        currentState.homeWorkouts.homeWorkoutSession.workoutId,
        dispatch,
        getState,
        workoutAssetHelper,
        currentState.homeWorkouts.homeWorkoutSession,
      );
    }
  } catch (error) {
    Logger.notify(error);
    dispatch({
      type: SAVE_HOME_WORKOUT_SETTINGS_FAILURE,
      payload: error,
    });
  }
};

export const closeInstructorAndDiscardChanges = () => async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  const currentState = getState() as IAppStore;
  dispatch({
    type: CLOSE_INSTRUCTOR_AND_DISCARD_CHANGES,
    payload: {
      instructor: currentState.homeWorkouts.homeWorkoutSession.workoutSettings.homeWorkoutInstructor,
      instructorList: currentState.homeWorkouts.instructorList,
    },
  });
};

export const closeSetupScreen = (fromSetupScreen?: DeviceSetupScreens) => ({
  type: CLOSE_SETUP_SCREEN,
  payload: {
    fromSetupScreen,
  },
  ...analyicsForAction(CLOSE_SETUP_SCREEN),
});

const loadWorkoutVideo = async (
  workoutId: string,
  sequence: SessionSequenceEntry,
  workoutAssetHelper: IWorkoutAssetHelper,
  errorRecoverer: () => Promise<void>,
  dispatch: Dispatch<any>,
) => {
  try {
    const videoUrl = await workoutAssetHelper.getVideoFileUrl(
      workoutId,
      sequence.videoFile,
      sequence.exerciseVersion,
    );
    dispatch({
      type: LOAD_WORKOUT_VIDEO_SUCCESS,
      payload: {
        videoUrl,
      },
    });
  } catch (error) {
    Logger.notify(
      new Error(
        `Unable to play next video videoFile: ${sequence.videoFile}, exerciseVersion: ${sequence.exerciseVersion}`,
      ),
      (report: any) => {
        report.errorClass = 'Home workout video not cached';
        report.errorMessage = error;
      },
    );
    dispatch({
      type: LOAD_WORKOUT_VIDEO_FAILURE,
      payload: {
        error,
      },
    });
    await errorRecoverer();
  }
};

const playAudioTip = async (
  workoutSession: HomeWorkoutSequence,
  fileName: string,
  exerciseVersion: string,
  voiceVolume: number,
  shouldCacheThisAudioTip: boolean | undefined,
  audioHelper: IAudioHelper,
  workoutAssetHelper: IWorkoutAssetHelper,
  dispatch: Dispatch<any>,
) => {
  try {
    if (AppState.currentState === 'background') {
      Logger.leaveBreadcrumb(`skipping audio tip ${fileName} app is in the background`);
      return;
    }

    const url = await workoutAssetHelper.getAudioFileUrl(
      workoutSession,
      fileName,
      exerciseVersion,
    );
    audioHelper.playTip(fileName, url, voiceVolume / 100, shouldCacheThisAudioTip)
      .catch((e) => Logger.notify(e));
  } finally {
    dispatch({
      type: AUDIO_TIP_PLAYED,
    });
  }
};

const mapHeartRatePointsToRawData = (
  heartRatePoints: IHeartRate[],
) => heartRatePoints.map((heartRateData: IHeartRate) => [
  heartRateData.heartRate,
  heartRateData.timestamp,
  heartRateData.source,
]);

const startAssetCaching = async (
  userProfile: UserProfile,
  workoutSession: HomeWorkoutSequence,
  currentIndex: number,
  workoutAssetHelper: IWorkoutAssetHelper,
  dispatch: Dispatch<any>,
  getState: () => IAppStore,
) => {
  await AudioHelper.clearCachedItems('startAssetCaching');
  workoutAssetHelper.cacheWorkoutAssets(
    workoutSession,
    currentIndex,
    NUMBER_OF_SEQUENCES_TO_PRELOAD,
    (sequenceIndexes: number[]) => {
      dispatch({
        type: WORKOUT_VIDEO_CACHED,
        payload: {
          sequenceIndexes,
        },
      });
    },
    () => {
      WorkoutAssetHelper.stopCurrentRunningCachingProcess();
      Logger.leaveBreadcrumb('Unable to download workout videos', {});
      dispatch({
        type: OPEN_HOME_WORKOUT_FAILURE,
        payload: 'Unable to download your workout videos',
      });
    },
    (progress: number) => {
      dispatch({
        type: ON_PRELOAD_OF_EXERCISES_PROGRESS_UPDATED,
        payload: progress,
      });
    },
  );
  startHomeWatchAppWorkout(
    userProfile,
    workoutSession.workoutSettings.activityType,
    dispatch,
    getState,
  ).catch((error) => Logger.leaveBreadcrumb('Failed to start connected apps', { error }));
};

async function recoverFromLoadWorkoutVideoFailure(
  audioHelper: IAudioHelper,
  bookingServiceClient: BookingServiceClient,
  workoutAssetHelper: IWorkoutAssetHelper,
  homeWorkouts: IHomeWorkoutsStoreState,
  dispatch: Dispatch<any>,
  getState: () => IAppStore,
) {
  if (!homeWorkouts.homeWorkoutSession) {
    return;
  }
  if (!homeWorkouts.userProfile) {
    Logger.notify(new Error('cannot recoverFromLoadWorkoutVideoFailure, userProfile is not set'));
    return;
  }

  Logger.leaveBreadcrumb('Recovering from workout video failure', {});
  dispatch({
    type: RESTART_ASSET_CACHING,
    payload: {},
  });
  WorkoutAssetHelper.stopCurrentRunningCachingProcess();

  Logger.debug('Pausing workout', {});
  const pause = workoutScreenTapped(audioHelper, bookingServiceClient);
  await pause(dispatch, getState);

  Logger.debug('Restart asset caching', {});
  const currentIndex = homeWorkouts.currentSessionSequenceInfo?.currentIndex ?? 0;
  const { userProfile, homeWorkoutSession } = homeWorkouts;
  await startAssetCaching(userProfile, homeWorkoutSession, currentIndex, workoutAssetHelper, dispatch, getState);
}

const restartBeltProcessIfMetricsHasGoneStale = (
  currentState: IAppStore,
  newState: IAppStore,
  dispatch: any,
) => {
  const shouldRestartBluetoothService = !newState.homeWorkouts.inOrPastWarmdown
    && !currentState.heartRateMonitor.metricDataHasGoneStale && newState.heartRateMonitor.metricDataHasGoneStale;
  const userProfile = currentState.homeWorkouts.userProfile as UserProfile;
  if (shouldRestartBluetoothService) {
    dispatch(StartListeningForHeartRateData(
      userProfile.heartRateMonitors?.home,
      userProfile,
      BluetoothServiceSingleton.getBluetoothService,
    ));
  }
};

const requestAndroidLocationPermissionForBluetooth = async (
  userProfile: UserProfile,
) => {
  if (userProfile.heartRateMonitors?.home) {
    await Permissions.requestLocationPermission();
  }
};

export const changeOrientation = (orientation: Orientation) => ({
  type: CHANGE_ORIENTATION,
  payload: {
    orientation,
  },
});

async function startHeartRateMonitorScan(userProfile: UserProfile, dispatch: any) {
  if (!userProfile.heartRateMonitors?.home) {
    return;
  }
  await requestAndroidLocationPermissionForBluetooth(userProfile);
  dispatch(StartListeningForHeartRateData(
    userProfile.heartRateMonitors.home,
    userProfile,
    BluetoothServiceSingleton.getBluetoothService,
  ));

  const bluetoothService = BluetoothServiceSingleton.getBluetoothService();
  bluetoothService.onBluetoothStateChange((state: BluetoothState) => {
    Logger.leaveBreadcrumb('Bluetooth state change:', {
      state,
    });
    if (state === BluetoothState.PoweredOn) {
      dispatch(StartListeningForHeartRateData(
        userProfile.heartRateMonitors.home,
        userProfile,
        BluetoothServiceSingleton.getBluetoothService,
      ));
      dispatch({
        type: HEART_RATE_AUTO_CONNECTED,
        ...analyicsForAction(HEART_RATE_AUTO_CONNECTED),
      });
    }
  });
}

async function onWarmupOrFirstExerciseJustStartedPlaying(state: IAppStore, dispatch: any) {
  await groupCallDisableTalkingAndStartMusic(state, dispatch);
  dispatch({ type: ON_WARMUPORFIRSTEXERCISE_JUST_STARTED_PLAYING });
}

async function onWarmdownHasStartedPlaying(
  bookingServiceClient: BookingServiceClient,
  state: IAppStore,
  dispatch: any,
) {
  if (state.homeWorkouts.shouldApplyWarmdownState) {
    dispatch(startWarmdown(bookingServiceClient));
  }
}

export const startWarmdown = (bookingServiceClient: BookingServiceClient) => (
  async (dispatch: (action: any) => void, getState: () => IAppStore) => {
    const state = getState();
    if (!state.homeWorkouts.homeWorkoutId) {
      return;
    }

    try {
      dispatch({ type: HOME_WARMDOWN_STARTED_REQUEST });
      await bookingServiceClient.homeWarmdownStarted(state.homeWorkouts.homeWorkoutId);
      dispatch({ type: HOME_WARMDOWN_STARTED_SUCCESS });
    } catch (e) {
      Logger.notify(new Error('Error setting homeWarmdownStarted'), (report: any) => {
        report.errorClass = 'Home workout';
        report.errorMessage = 'homeWarmdownStarted failed, will be retried on the next updateWorkoutScreen loop';
        report.reason = (e as Error)?.message || e;
      });
      dispatch({ type: HOME_WARMDOWN_STARTED_FAILURE });
    }
  }
);

async function groupCallDisableTalkingAndStartMusic(state: IAppStore, dispatch: any) {
  const { isGroupWorkout } = state.homeWorkouts;
  const { isStreamingWorkout } = state.homeWorkouts.homeWorkoutSession;
  if (!isGroupWorkout) {
    return;
  }

  if (!isStreamingWorkout && state.groupCalling.talkingEnabled) {
    Logger.debug('Workout action:startMusicAndDisableTalkingAudioOnGroupWorkoutStart disable agora');
    await disableAgoraAudio(state, dispatch);
  }
  dispatch(genericMusicPlay());
  Logger.debug('Workout action:startMusicAndDisableTalkingAudioOnGroupWorkoutStart done');
}

function stopMusicAndEnableTalkingIfWorkoutEnded(stateBeforeTick: IAppStore, stateAfterTick: IAppStore, dispatch: any) {
  const hasWorkoutJustEnded = !stateBeforeTick.homeWorkouts.isPastWarmdown
    && stateAfterTick.homeWorkouts.isPastWarmdown;
  const { isGroupWorkout } = stateAfterTick.homeWorkouts;
  const isStreamingWorkout = stateAfterTick.homeWorkouts.homeWorkoutSession?.isStreamingWorkout;
  const shouldStopMusicAndEnableTalkingIfWorkoutEnded = (hasWorkoutJustEnded && isGroupWorkout && !isStreamingWorkout);
  if (shouldStopMusicAndEnableTalkingIfWorkoutEnded) {
    Logger.debug('Workout action:stopMusicAndEnableTalkingIfWorkoutEnded');
    stopMusicAndEnableAudio(stateAfterTick, dispatch)
      .catch((e) => Logger.notify(e));
  }
}

async function onPreloadOfExercisesCompleted(newState: IAppStore, dispatch: any) {
  Logger.debug('Workout action:onPreloadOfExercisesCompleted');
  const { userProfile } = newState.homeWorkouts;
  if (userProfile && userProfile.heartRateMonitors?.home && !newState.heartRateMonitor.isScanningBelt) {
    startHeartRateMonitorScan(userProfile, dispatch)
      .catch((e) => Logger.notify(e));
  }
  initialiseMusicOnceOffBeforeWorkoutStart(newState, dispatch);
  dispatch({ type: ON_PRELOAD_OF_EXERCISES_COMPLETED });
}

function initialiseMusicOnceOffBeforeWorkoutStart(newState: IAppStore, dispatch: any) {
  if (newState.music.musicInitialiseState === 'not-started') {
    Logger.debug('Workout action:initialiseMusicOnceOffBeforeWorkoutStart');
    dispatch(initialiseMusicAndStartNonGeneric());
  }
}

async function updateWorkoutSettings(
  bookingServiceClient: BookingServiceClient,
  workoutId: string,
  dispatch: any,
  getState: any,
  workoutAssetHelper: IWorkoutAssetHelper,
  homeWorkoutSession: HomeWorkoutSequence,
) {
  WorkoutAssetHelper.stopCurrentRunningCachingProcess();
  const timezone = moment.tz.guess();
  const updatedWorkoutSession = await bookingServiceClient.getHydratedHomeWorkout(workoutId, timezone);
  const shouldReloadMusic = homeWorkoutSession.workoutSettings.cardioOrResistance
    !== updatedWorkoutSession.workoutSettings.cardioOrResistance;
  if (shouldReloadMusic) {
    loadTracks(updatedWorkoutSession.workoutSettings.cardioOrResistance, dispatch)
      .catch((e) => Logger.notify(e));
  }
  dispatch({
    type: SAVE_HOME_WORKOUT_SETTINGS_SUCCESS,
    payload: {
      updatedWorkoutSession,
      now: new Date(),
    },
    ...analyicsForAction(SAVE_HOME_WORKOUT_SETTINGS_SUCCESS),
  });

  dispatch({
    type: OPEN_PERSONALISE_WORKOUT_SETUP_SCREEN,
    payload: {
      screen: PersonaliseSetupScreens.Overview,
    },
  });

  const newState = getState() as IAppStore;
  const currentIndex = newState.homeWorkouts.currentSessionSequenceInfo
    ? newState.homeWorkouts.currentSessionSequenceInfo.currentIndex : 0;

  if (!newState.homeWorkouts.userProfile) {
    Logger.notify(new Error('cannot updateWorkoutSettings, userProfile is not set'));
    return;
  }
  const { userProfile } = newState.homeWorkouts;
  await startAssetCaching(userProfile, updatedWorkoutSession, currentIndex, workoutAssetHelper, dispatch, getState);
}

const stopMusicAndEnableAudio = async (newState: IAppStore, dispatch: any) => {
  await cleanUpMusic(dispatch);
  await enableAgoraAudio(dispatch, newState);
};

export const CalculatedMetricsHasChanged = async (
  bookingServiceClient: BookingServiceClient,
  state: IAppStore,
  dispatch: Dispatch,
) => {
  const { userProfile } = state.homeWorkouts;
  const isStreamingWorkout = selectIsStreamingWorkout(state);
  const streamingWorkoutHostId = selectStreamingHostWorkoutId(state);

  if (!userProfile) {
    return;
  }

  try {
    dispatch({
      type: UPDATE_LEADERBOARD_REQUEST,
    });
    const msRemainingInWorkout = state.homeWorkouts.currentSessionSequenceInfo
      ? state.homeWorkouts.currentSessionSequenceInfo.msRemainingInWorkout : 0;
    const leaderboardResponse = await bookingServiceClient.updateHomeGroupClassAndGetLeaderboard(
      state.homeWorkouts.homeWorkoutId,
      state.heartRateMonitor.metricResult,
      msRemainingInWorkout,
    );
    const participantIdsToCreateOrUpdateFromLeaderboardRows = Object.keys(leaderboardResponse.groupCallLeaderboardRows)
      .filter((userProfileId: string) => !isStreamingWorkout || streamingWorkoutHostId === userProfileId);
    Logger.debug('CalculatedMetricsHasChanged', {
      leaderboardResponse,
      participantIdsToCreateOrUpdateFromLeaderboardRows,
    });
    dispatch({
      type: UPDATE_LEADERBOARD_SUCCESS,
      payload: {
        leaderboardResponse,
        date: new Date(),
        participantIdsToCreateOrUpdateFromLeaderboardRows,
      },
    });
  } catch (error) {
    Logger.leaveBreadcrumb('Leaderboard failed to update', error);
    dispatch({
      type: UPDATE_LEADERBOARD_FAILURE,
      payload: {
        error,
      },
    });
  }
};

export enum CameraPermissionRequest {
  ALREADY_GRANTED,
  NEWLY_GRANTED,
  DENIED,
}

const manageCameraPermissions = async (): Promise<CameraPermissionRequest> => {
  const hasCameraPermission = await Permissions.isCameraPermissionGranted();

  if (hasCameraPermission) {
    return CameraPermissionRequest.ALREADY_GRANTED;
  }

  const permissionRequest = await Permissions.requestCameraPermission();
  if (permissionRequest) {
    return CameraPermissionRequest.NEWLY_GRANTED;
  }

  return CameraPermissionRequest.DENIED;
};

const initMirrorLocalStorage = (permissionRequest: CameraPermissionRequest): void => {
  if (permissionRequest === CameraPermissionRequest.DENIED) {
    return Storage.set('defaultMirrorState', JSON.stringify(false));
  }
  if (permissionRequest === CameraPermissionRequest.NEWLY_GRANTED) {
    return Storage.set('defaultMirrorState', JSON.stringify(true));
  }
};

export const mirrorSetupScreenOpened = () => (
  async (dispatch: Dispatch<any>) => {
    const cameraPermissionRequest = await manageCameraPermissions();
    initMirrorLocalStorage(cameraPermissionRequest);

    dispatch({
      type: MIRROR_SETUP_SCREEN_OPENED,
      payload: {
        cameraPermissionRequest,
      },
      ...analyicsForAction(MIRROR_SETUP_SCREEN_OPENED),
    });
  }
);

export const enableMirror = () => async (dispatch: Dispatch<any>) => {
  await Storage.set('defaultMirrorState', JSON.stringify(true));
  const permissionGranted = await Permissions.isCameraPermissionGranted();
  if (permissionGranted) {
    dispatch({
      type: ENABLE_MIRROR,
      ...analyicsForAction(ENABLE_MIRROR),
    });
  }
};

export const disableMirror = () => async (dispatch: Dispatch<any>) => {
  await Storage.set('defaultMirrorState', JSON.stringify(false));
  dispatch({
    type: DISABLE_MIRROR,
    ...analyicsForAction(DISABLE_MIRROR),
  });
};

export const calculatedLeaderboardLayoutChanged = (amountOfRows: number) => ({
  type: LEADERBOARD_LAYOUT_CHANGED,
  payload: {
    amountOfRows,
  },
});

export const activateLeaderBoard = () => async (dispatch: Dispatch<any>) => {
  await Storage.set('defaultLeaderBoardState', JSON.stringify(true));
  dispatch({
    type: ACTIVATE_LEADERBOARD,
  });
};

export const deactivateLeaderBoard = () => async (dispatch: Dispatch<any>) => {
  await Storage.set('defaultLeaderBoardState', JSON.stringify(false));
  dispatch({
    type: DEACTIVATE_LEADERBOARD,
  });
};

export const inviteFriendsPressed = (
  bookingServiceClient: BookingServiceClient,
  userProfile: UserProfile,
  t: (key: string, options?: any) => string,
) => async (
  dispatch: Dispatch<IInviteFriendsRequest | IInviteFriendsSuccess | IInviteFriendsFailure>,
  getState: () => IAppStore,
) => {
  try {
    dispatch({
      type: INVITE_FRIENDS_REQUEST,
      ...analyicsForAction(INVITE_FRIENDS_REQUEST),
    });
    const { homeWorkouts } = getState();
    const workoutSession = homeWorkouts.homeWorkoutSession!;
    const { link: shareLink } = await bookingServiceClient.getOpenInviteLinkForWorkout(workoutSession.workoutId);
    shareOpenInviteLink(
      t,
      shareLink,
      userProfile,
      new Date(workoutSession.startTimeOfFirstExercise),
    )
      .catch((e) => Logger.notify(e));
    dispatch({ type: INVITE_FRIENDS_SUCCESS });
  } catch {
    dispatch({ type: INVITE_FRIENDS_FAILURE });
  }
};

interface IInviteFriendsRequest {
  type: typeof INVITE_FRIENDS_REQUEST;
}

interface IInviteFriendsSuccess {
  type: typeof INVITE_FRIENDS_SUCCESS;
}

interface IInviteFriendsFailure {
  type: typeof INVITE_FRIENDS_FAILURE;
}

interface IPreWorkoutCancelRequest {
  type: typeof PRE_WORKOUT_CANCEL_REQUEST;
  payload: {
    deleteBookingId: string,
  };
}

interface IPreWorkoutCancelSuccess {
  type: typeof PRE_WORKOUT_CANCEL_SUCCESS;
}

interface IPreWorkoutCancelFailure {
  type: typeof PRE_WORKOUT_CANCEL_FAILURE;
}

export interface IVolumeChangedAction {
  type: typeof BEEP_VOLUME_CHANGED | typeof VOICE_VOLUME_CHANGED;
  payload: {
    volume: number,
  };
}
